/**
 * BootstrapPage — first-time Super Admin setup.
 *
 * Shown once, immediately after app install, when no DT OS employees exist yet.
 * The user must enter the Bootstrap Token (generated at OAuth install and available
 * in the Cloudflare D1 dashboard under the `shops` table, `bootstrap_token` column).
 *
 * After submission the bootstrap window closes permanently.
 * The created admin is auto-logged in and the session is passed to onComplete().
 */

import { useState }      from 'react'
import {
  Banner, BlockStack, Button, Card,
  FormLayout, Page, Text, TextField,
} from '@shopify/polaris'
import { ENV }            from '../config/environment'
import { getShop }        from '../sdk/shop'
import { storeDtOsToken } from '../hooks/useAppSession'
import type { EmployeeSession } from '../hooks/useAppSession'

type Props = {
  onComplete: (session: EmployeeSession) => void
}

export function BootstrapPage({ onComplete }: Props) {
  const [bootstrapToken, setBootstrapToken] = useState('')
  const [displayName,    setDisplayName]    = useState('')
  const [username,       setUsername]       = useState('')
  const [password,       setPassword]       = useState('')
  const [confirm,        setConfirm]        = useState('')
  const [busy,           setBusy]           = useState(false)
  const [error,          setError]          = useState<string | null>(null)

  async function handleSubmit() {
    if (!bootstrapToken.trim() || !displayName.trim() || !username.trim() || !password || !confirm) return
    if (password !== confirm) { setError('Passwords do not match.'); return }
    if (password.length < 8)  { setError('Password must be at least 8 characters.'); return }

    const shop = getShop()
    if (!shop) { setError('Could not determine shop — please reload.'); return }

    setBusy(true)
    setError(null)

    try {
      const url = new URL(`${ENV.API_BASE_URL}/auth/bootstrap`)
      url.searchParams.set('shop', shop)

      const res = await fetch(url.toString(), {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bootstrapToken: bootstrapToken.trim(),
          username:       username.trim(),
          password,
          displayName:    displayName.trim(),
        }),
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` })) as { error: string }
        if (res.status === 409) setError('Setup already completed — please sign in.')
        else if (res.status === 403) setError('Invalid Bootstrap Token — check the D1 dashboard.')
        else if (res.status === 400 && body.error === 'password_too_short') setError('Password must be at least 8 characters.')
        else setError(body.error ?? 'Setup failed — please try again.')
        return
      }

      const data = await res.json() as {
        token:          string
        employeeId:     string
        displayName:    string
        tenantRole:     'admin' | 'member'
        mustChangePass: boolean
        memberships:    EmployeeSession['memberships']
        opsPermissions: Record<string, unknown> | null
      }

      storeDtOsToken(data.token)
      onComplete({
        type:           'employee',
        employeeId:     data.employeeId,
        displayName:    data.displayName,
        tenantRole:     data.tenantRole,
        mustChangePass: data.mustChangePass,
        memberships:    data.memberships,
        opsPermissions: data.opsPermissions ?? null,
      })
    } catch {
      setError('Unable to connect — please try again.')
    } finally {
      setBusy(false)
    }
  }

  const canSubmit = bootstrapToken.trim() && displayName.trim() && username.trim() && password && confirm && !busy

  return (
    <Page narrowWidth title="DT OS — Initial Setup">
      <Card>
        <BlockStack gap="400">
          <BlockStack gap="100">
            <Text as="p" fontWeight="semibold">Welcome to DT OS</Text>
            <Text as="p" tone="subdued">
              Create your administrator account to get started.
              You will need the Bootstrap Token from the Cloudflare D1 dashboard
              (shops table → bootstrap_token column).
            </Text>
          </BlockStack>

          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>
          )}

          <FormLayout>
            <TextField
              label="Bootstrap Token"
              value={bootstrapToken}
              onChange={setBootstrapToken}
              autoComplete="off"
              requiredIndicator
              helpText="Found in D1 dashboard → dt-os-engine → shops table → bootstrap_token."
              monospaced
            />
            <TextField
              label="Your name"
              value={displayName}
              onChange={setDisplayName}
              autoComplete="name"
              requiredIndicator
              helpText="Displayed to your team inside DT OS."
            />
            <TextField
              label="Username"
              value={username}
              onChange={setUsername}
              autoComplete="username"
              requiredIndicator
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              requiredIndicator
              helpText="At least 8 characters."
            />
            <div onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' && canSubmit) handleSubmit() }}>
              <TextField
                label="Confirm password"
                type="password"
                value={confirm}
                onChange={setConfirm}
                autoComplete="new-password"
                requiredIndicator
                error={confirm && password !== confirm ? 'Passwords do not match' : undefined}
              />
            </div>
          </FormLayout>

          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={busy}
            disabled={!canSubmit}
          >
            Create admin account
          </Button>
        </BlockStack>
      </Card>
    </Page>
  )
}
